/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
// Localization
import {IntlProvider} from 'react-intl';
import {useHistory, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';

import {SkipNavContent} from '@chakra-ui/skip-nav';
import {useQuery, useQueryClient} from '@tanstack/react-query';

import {useAccessToken, useCategory, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react';
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components';
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import AboveHeader from '@amblique/king-living/app/components/_app/partials/above-header';
import GtmHead from '@amblique/king-living/app/components/gtm/gtm-head';
import GtmPageView from '@amblique/king-living/app/components/gtm/gtm-page-view';
// Local Project Components
import Header from '@salesforce/retail-react-app/app/components/header';
import {ListMenuContent} from '@salesforce/retail-react-app/app/components/list-menu';
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner';
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';
import ScrollToTop from '@amblique/king-living/app/components/scroll-to-top';
import Seo from '@salesforce/retail-react-app/app/components/seo';
// Chakra
import {
  Box, Center, Fade, Spinner, useDisclosure, useStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal';
import CartFlyout from '@amblique/king-living/app/components/cart-flyout';
import SearchModal from '@amblique/king-living/app/components/search-modal';
import {storeContinueShoppingPath} from '@amblique/king-living/app/pages/checkout/util/checkout-context';
import Footer from '@amblique/king-living/app/components/footer';
import {ShowroomSelectionPopup} from '@amblique/king-living/app/components/showroom';
import {SkipToContentLink, SKIP_TO_CONTENT_SELECTOR} from '@amblique/king-living/app/components/skip-to-content-link';
import {useHeaderTabToSkipLink} from '@amblique/king-living/app/hooks/use-header-tab-to-skip-link';
import {useShowroomSync} from '@amblique/king-living/app/hooks/use-showroom-sync';
import {useWarehouseInventoryOnLoad} from '@amblique/king-living/app/hooks/use-warehouse-inventory-on-load';
import {useLocaleSync} from '@amblique/king-living/app/hooks/use-locale-sync';
import {useCrmSiteSync} from '@amblique/king-living/app/hooks/use-crm-site-sync';

// HOCs
import {
  withCommerceSdkReact,
} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react';
import {
  ACTIVE_DATA_ENABLED, DEFAULT_LOCALE,
  HOME_HREF, THEME_COLOR,
} from '@salesforce/retail-react-app/app/constants';
import {DEFAULT_SITE_TITLE} from '@amblique/king-living/app/constants';
// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts';
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data';
import {AddToCartModalProvider} from '@salesforce/retail-react-app/app/hooks/use-add-to-cart-modal';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
// Hooks
import {AuthModal, useAuthModal} from '@salesforce/retail-react-app/app/hooks/use-auth-modal';
import {DntNotification, useDntNotification} from '@amblique/king-living/app/hooks/use-dnt-notification';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useUpdateShopperContext} from '@salesforce/retail-react-app/app/hooks/use-update-shopper-context';
import CheckoutHeader from '@salesforce/retail-react-app/app/pages/checkout/partials/checkout-header';
import {fetchTranslations, getTargetLocale} from '@salesforce/retail-react-app/app/utils/locale';
import logger from '@salesforce/retail-react-app/app/utils/logger-instance';
import {resolveLocaleFromUrl} from '@salesforce/retail-react-app/app/utils/site-utils';
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url';
// Others
import {flatten, isServer, watchOnlineStatus} from '@salesforce/retail-react-app/app/utils/utils';
// Geo-redirect
import {useCanadaProvincialLocale} from '@amblique/king-living/app/hooks/use-canada-provincial-locale';
import {useGeoRedirect} from '@amblique/king-living/app/hooks/use-geo-redirect';
import {RegionRedirectModal} from '@amblique/king-living/app/components/region-redirect-modal';

// AMB Components
import {MegaMenuDesktop} from '@amblique/king-living/app/components/mega-menu/desktop/mega-menu-desktop';
import {MegaMenuMobile} from '@amblique/king-living/app/components/mega-menu/mobile/mega-menu-mobile';
import {useContentfulHeader} from '@amblique/king-living/app/hooks/use-contentful-header';
import {BlurOverlayProvider} from '@amblique/king-living/app/contexts/blur-overlay-context';
import {CalendlyTriggerProvider} from '@amblique/king-living/app/contexts/calendly-showroom-context';
import {CartFlyoutProvider} from '@amblique/king-living/app/contexts/cart-flyout-context';
import {HeaderProvider, useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
// Utils
import {
  ensureArray,
  getHeaderStyleForPath,
  getPreferredLocaleFromCookie,
  isHeaderOverlayMode,
  setPreferredLocaleToCookie,
} from '@amblique/king-living/app/utils/utils';
import {DISABLE_STICKY_PAGES} from '@amblique/king-living/app/constants/header';

const PlaceholderComponent = () => (
  <Center p="2">
    <Spinner size="lg" />
  </Center>
);

const DrawerMenuItemWithData = withCommerceSdkReact(
  ({itemComponent: ItemComponent, data, ...rest}) => (
    <Fade in={true}>
      <ItemComponent {...rest} item={data} itemComponent={DrawerMenuItemWithData} />
    </Fade>
  ),
  {
    hook: useCategory,
    queryOptions: ({item}) => ({
      parameters: {
        id: item.id,
      },
    }),
    placeholder: PlaceholderComponent,
  }
);

const ListMenuContentWithData = withCommerceSdkReact(
  ({data, ...rest}) => (
    <Fade in={true}>
      <ListMenuContent {...rest} item={data} />
    </Fade>
  ),
  {
    hook: useCategory,
    queryOptions: ({item}) => ({
      parameters: {
        id: item.id,
        levels: 2,
      },
    }),
    placeholder: PlaceholderComponent,
  }
);

/**
 * Inner wrapper that reads header context to apply hide-on-scroll transform.
 * Must be rendered inside HeaderProvider.
 */
const StickyHeaderBox = React.forwardRef(({isStickyDisabled, isSearchBoxOpen, children, ...rest}, ref) => {
  const styles = useStyleConfig('App');
  const {isHeaderHidden} = useHeaderContext();

  return (
    <Box
      ref={ref}
      {...styles.headerWrapper}
      position={isStickyDisabled ? 'static' : 'sticky'}
      transform={isHeaderHidden ? 'translateY(-100%)' : 'translateY(0)'}
      transition="transform 0.2s ease"
      zIndex={isSearchBoxOpen ? 1500 : 'sticky'}
      data-header-hidden={isHeaderHidden ? 'true' : undefined}
      data-header-wrapper
      {...rest}
    >
      {children}
    </Box>
  );
});

StickyHeaderBox.displayName = 'StickyHeaderBox';
StickyHeaderBox.propTypes = {
  children: PropTypes.node,
  isSearchBoxOpen: PropTypes.bool,
  isStickyDisabled: PropTypes.bool,
};

const App = (props) => {
  const {children} = props;
  // const {data: categoriesTree} = useCategory({
  //   parameters: {id: CAT_MENU_DEFAULT_ROOT_CATEGORY, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
  // });
  // const categories = flatten(categoriesTree || {}, 'categories');
  const {getTokenWhenReady} = useAccessToken();
  const queryClient = useQueryClient();
  const appOrigin = useAppOrigin();
  const activeData = useActiveData();
  const history = useHistory();
  const location = useLocation();
  const authModal = useAuthModal();
  const dntNotification = useDntNotification();
  const {site, locale, buildUrl} = useMultiSite();
  const siteId = site?.id;
  const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer();

  // Fetch Contentful header by siteId with nested data (Header → NavigationMenu → Level 1 Menu Items)
  const {
    data: contentfulHeader,
    isLoading: isNavigationMenuLoading,
  } = useContentfulHeader(siteId, {
    enabled: !!siteId,
    onError: (error) => {
      console.error('[DEBUG] Header Contentful - Error:', {
        error,
        message: error?.message,
        status: error?.status,
        siteId,
      });
    },
  });

  // Compute header configured state based on current route and Contentful config
  // This determines the initial header style (solid, gradient, transparent) for each page
  // Scroll behavior, hover, and mega menu overrides are handled by HeaderProvider/useHeaderState
  const headerConfiguredState = useMemo(() => {
    return getHeaderStyleForPath(contentfulHeader?.headerStyle, location.pathname);
  }, [contentfulHeader?.headerStyle, location.pathname]);

  // Reference for header wrapper element to measure its height
  const headerWrapperRef = useRef(null);

  useHeaderTabToSkipLink();

  // Check if sticky is disabled for current route
  const isStickyDisabled = useMemo(() => {
    return DISABLE_STICKY_PAGES.some((path) => location.pathname.includes(path));
  }, [location.pathname]);

  // Determine if the header should overlay content (transparent/gradient modes)
  // In overlay mode, main content extends behind the header
  // Only applies when sticky is NOT disabled
  const isOverlayMode = useMemo(() => {
    return !isStickyDisabled && isHeaderOverlayMode(headerConfiguredState);
  }, [isStickyDisabled, headerConfiguredState]);

  const [isOnline, setIsOnline] = useState(true);
  const styles = useStyleConfig('App');
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    isOpen: isOpenStoreLocator,
    onOpen: onOpenStoreLocator,
    onClose: onCloseStoreLocator,
  } = useDisclosure();

  const {
    isOpen: isOpenSearchBox,
    onOpen: onOpenSearchBox,
    onClose: onCloseSearchBox,
  } = useDisclosure();


  const targetLocale = getTargetLocale({
    getUserPreferredLocales: () => {
      // Priority order for locale selection:
      // 1. URL path locale (e.g., /fr/home → fr-CA)
      // 2. Saved preference in cookie (persists across sessions)
      // 3. Default locale for the site
      //
      // Derive URL locale from the path (not only MultiSite context) so targetLocale matches SSR
      // and the translations query key after hydration on MRT.
      let urlLocaleId = locale?.id;

      try {
        const pathForLocale = `${location.pathname}${location.search || ''}`;
        const resolvedFromPath = resolveLocaleFromUrl(pathForLocale);

        if (resolvedFromPath?.id) {
          urlLocaleId = resolvedFromPath.id;
        }
      } catch {
        // keep locale from context
      }

      const localesFromUrl = urlLocaleId ? [urlLocaleId] : [];

      // Check cookie for saved preference (only client-side)
      const cookieLocale = !isServer ? getPreferredLocaleFromCookie(site?.id) : null;
      const localesFromCookie = cookieLocale ? [cookieLocale] : [];

      // Combine preferences in priority order
      // URL takes precedence, then cookie, then default
      return [...localesFromUrl, ...localesFromCookie, DEFAULT_LOCALE].filter(Boolean);
    },
    l10nConfig: site.l10n,
  });

  // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
  // However, if the file is missing, the App would render a 404 page.
  const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
    location?.pathname
  );

  // Fetch the translation message data using the target locale.
  const {data: messages} = useQuery({
    queryKey: ['app', 'translations', 'messages', targetLocale],
    queryFn: () => {
      if (is404ForMissingTranslationFile) {
        // Return early to prevent an infinite loop
        // Otherwise, it'll continue to fetch the missing translation file again
        return {};
      }

      return fetchTranslations(targetLocale, appOrigin);
    },
    // Run on the client so messages load if React Query hydration misses the translations query.
    enabled: !!targetLocale,
    staleTime: 0,
    refetchOnMount: true,
  });

  // After hydration, force a client refetch so Network shows fr-CA.json and IntlProvider gets messages
  // if the serialized React Query state was empty or mismatched (common on MRT).
  useEffect(() => {
    if (isServer || !targetLocale) return;

    queryClient.invalidateQueries({
      queryKey: ['app', 'translations', 'messages', targetLocale],
    });
  }, [queryClient, targetLocale]);

  // Used to conditionally render header/footer for checkout page
  const isCheckout = /\/checkout$/.test(location?.pathname);
  const isLogin = /\/login$/.test(location?.pathname);
  const isRegister = /\/registration$/.test(location?.pathname);
  const isResetPassword = /\/reset-password$/.test(location?.pathname);
  const isResetPasswordLanding = /\/reset-password-landing$/.test(location?.pathname);
  const isPasswordlessLogin = /\/passwordless-login-landing$/.test(location?.pathname);
  const isAuth = isLogin || isRegister || isResetPassword || isResetPasswordLanding || isPasswordlessLogin;

  const cookieLocaleClient = !isServer ? getPreferredLocaleFromCookie(siteId) : null;

  const accountHasPreferredLocale = useMemo(() => {
    if (!customer?.isRegistered || !customer?.preferredLocale || !site?.l10n?.supportedLocales) {
      return false;
    }

    return site.l10n.supportedLocales.some(
      (l) => l.id === customer.preferredLocale || l.alias === customer.preferredLocale
    );
  }, [customer?.isRegistered, customer?.preferredLocale, site?.l10n?.supportedLocales]);

  // KINDW-710: only block on useCustomer() loading for registered users (account preferredLocale).
  // Guests do not fetch customer profile (useCustomer disabled); tying to isCustomerLoading can stall incorrectly.
  const customerIdentityReadyForProvincial = useMemo(() => {
    if (customer?.isGuest) {
      return true;
    }

    if (customer?.isRegistered) {
      return !isCustomerLoading;
    }

    return false;
  }, [customer?.isGuest, customer?.isRegistered, isCustomerLoading]);

  const waitForCustomerBeforeLocale =
    !isServer &&
    siteId === 'kl-ca' &&
    !cookieLocaleClient &&
    !customerIdentityReadyForProvincial;

  const skipProvincialGeoRun =
    isServer ||
    siteId !== 'kl-ca' ||
    !!cookieLocaleClient ||
    accountHasPreferredLocale ||
    isCheckout ||
    isAuth;

  const [provincialGeoComplete, setProvincialGeoComplete] = useState(false);

  const allowFirstVisitLocaleCookie =
    skipProvincialGeoRun || (!waitForCustomerBeforeLocale && provincialGeoComplete);

  useEffect(() => {
    if (skipProvincialGeoRun) {
      setProvincialGeoComplete(true);
    } else {
      setProvincialGeoComplete(false);
    }
  }, [skipProvincialGeoRun]);

  useLayoutEffect(() => {
    if (isServer || !isCheckout) return;

    document.querySelector(SKIP_TO_CONTENT_SELECTOR)?.focus({preventScroll: true});
  }, [isCheckout, location?.pathname]);

  // Client-side geo region hint + optional modal (browser Google Geolocation + server geocode). Checkout stays excluded
  // (payment flow); auth routes (login, registration, etc.) allow the region modal so visitors on
  // the wrong site/locale can switch before signing in.
  const {
    isRegionModalOpen,
    suggestion: regionSuggestion,
    dismissRegionSuggestion,
    confirmRegionSwitch,
  } = useGeoRedirect({
    enabled: !isServer && !isCheckout,
    debug: process.env.NODE_ENV === 'development',
  });

  // KINDW-710 / FSD (Option A): auto fr-CA in Quebec / en-CA elsewhere in Canada (client Google Geolocation).
  useCanadaProvincialLocale({
    buildUrl,
    enabled:
      !isServer &&
      siteId === 'kl-ca' &&
      !isCheckout &&
      !isAuth &&
      !cookieLocaleClient &&
      !accountHasPreferredLocale &&
      customerIdentityReadyForProvincial,
    location,
    onComplete: () => setProvincialGeoComplete(true),
    siteId,
    sitePreferences: site?.preferences,
  });

  // IM-003: client Google Geolocation → shipping get-methods → warehouse inventory cookies when empty
  useWarehouseInventoryOnLoad({
    enabled: !isServer && !isCheckout && !isAuth,
  });

  // 1) First visit (no cookie): persist current URL locale to cookie.
  // 2) Redirect when preferred locale (account if logged in, else cookie) differs from URL (KINDW-744).
  // 3) When logged in, account preferredLocale wins so login on /kl-ca redirects to /fr if account has fr-CA.
  // 4) Auth pages (login, registration, etc.) also get locale redirect so /kl-ca/login with fr_CA cookie → /kl-ca/fr/login.
  const DEBUG_LOCALE_REDIRECT = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (isServer || isCheckout) {
      return;
    }

    if (!site?.id || !site?.l10n?.supportedLocales?.length) {
      return;
    }

    const path = `${location.pathname}${location.search || ''}`;
    const supportedLocales = site.l10n.supportedLocales;
    let cookieLocale = getPreferredLocaleFromCookie(site.id);

    // First visit: no cookie yet → save current URL locale to cookie (after KINDW-710 provincial check when applicable).
    if (!cookieLocale && allowFirstVisitLocaleCookie) {
      try {
        const localeFromPath = resolveLocaleFromUrl(path);
        const urlLocaleId = localeFromPath?.id;
        const isSupported = supportedLocales.some(
          (l) => l.id === urlLocaleId || l.alias === urlLocaleId
        );

        if (urlLocaleId && isSupported) {
          setPreferredLocaleToCookie(site.id, urlLocaleId);
          cookieLocale = urlLocaleId;
        }
      } catch {
        // ignore parse errors
      }
    }

    // Preferred locale: account wins when logged in (so login redirects to account locale)
    const accountLocale =
      customer?.isRegistered && customer?.preferredLocale &&
      supportedLocales.some((l) => l.id === customer.preferredLocale || l.alias === customer.preferredLocale) ?
        customer.preferredLocale :
        null;
    const preferredLocale = accountLocale || cookieLocale;

    if (!preferredLocale) {
      return;
    }

    let localeFromPath;

    try {
      localeFromPath = resolveLocaleFromUrl(path);
    } catch {
      return;
    }

    const urlLocaleId = localeFromPath?.id;

    const matchedLocale = supportedLocales.find(
      (l) => l.id === preferredLocale || l.alias === preferredLocale
    );
    const urlMatchesPreferred = supportedLocales.some(
      (l) => (l.id === urlLocaleId || l.alias === urlLocaleId) &&
        (l.id === preferredLocale || l.alias === preferredLocale)
    );

    if (matchedLocale && !urlMatchesPreferred) {
      window.location.href = getPathWithLocale(preferredLocale, buildUrl, {disallowParams: ['refine']});
    }
  }, [
    allowFirstVisitLocaleCookie,
    location.pathname,
    location.search,
    site?.id,
    site?.l10n?.supportedLocales,
    buildUrl,
    customer?.isRegistered,
    customer?.preferredLocale
  ]);

  const {l10n} = site;
  // Get the current currency to be used through out the app
  const currency = locale.preferredCurrency || l10n.defaultCurrency;

  // Handle creating a new basket if there isn't one already assigned to the current
  // customer.
  const {data: basket} = useCurrentBasket();

  // Sync showroom preference between cookie and SFCC account
  // This hook auto-syncs when user logs in or selects a showroom
  useShowroomSync();

  // Sync locale preference between cookie and SFCC account
  // This hook auto-syncs when user logs in or changes language
  useLocaleSync();

  // Sync CRM account when user visits a different country site
  // Ensures CRM account exists for the current country (cross-country support)
  useCrmSiteSync();

  const updateBasket = useShopperBasketsMutation('updateBasket');
  const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket');

  useEffect(() => {
    // update the basket currency if it doesn't match the current locale currency
    if (basket?.currency && basket?.currency !== currency) {
      updateBasket.mutate({
        parameters: {basketId: basket.basketId},
        body: {currency},
      });
    }
  }, [basket?.currency]);

  useEffect(() => {
    // update the basket customer email
    if (
      basket &&
      customer?.isRegistered &&
      customer?.email &&
      customer?.email !== basket?.customerInfo?.email
    ) {
      updateCustomerForBasket.mutate({
        parameters: {basketId: basket.basketId},
        body: {
          email: customer.email,
        },
      });
    }
  }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email]);

  useEffect(() => {
    // Listen for online status changes.
    watchOnlineStatus((isOnline) => {
      setIsOnline(isOnline);
    });
  }, []);

  // Handle updating the shopper context
  useUpdateShopperContext();

  // ResizeObserver for header height measurement
  // This enables smooth overlay behavior by dynamically adjusting main content margin
  useEffect(() => {
    const headerEl = headerWrapperRef.current;

    if (!headerEl) return;

    // Variables to track resize state for smooth transitions
    let resizeTimeout;
    let isResizing = false;
    const mainEl = document.getElementById('app-main');

    const updateHeaderHeight = () => {
      const height = headerEl.offsetHeight;

      document.documentElement.style.setProperty('--header-height', `${height}px`);

      // Update main content margin based on overlay mode
      if (mainEl) {
        if (isOverlayMode) {
          mainEl.style.marginTop = `-${height}px`;
        } else {
          mainEl.style.marginTop = '0px';
        }
      }
    };

    const observer = new ResizeObserver(() => {
      // Temporarily disable transitions during resize for smooth experience
      if (!isResizing && mainEl) {
        isResizing = true;
        mainEl.style.transition = 'none';
      }

      // Clear previous timeout
      clearTimeout(resizeTimeout);

      // Update height immediately
      updateHeaderHeight();

      // Re-enable transitions after resize settles
      resizeTimeout = setTimeout(() => {
        if (mainEl) {
          mainEl.style.transition = 'margin-top 0.3s ease-out';
        }
        isResizing = false;
      }, 150);
    });

    observer.observe(headerEl);

    // Initial height measurement
    updateHeaderHeight();

    // Enable transitions after initial render
    if (mainEl) {
      mainEl.style.transition = 'margin-top 0.3s ease-out';
    }

    return () => {
      observer.disconnect();
      clearTimeout(resizeTimeout);
    };
  }, [isOverlayMode]);

  useEffect(() => {
    // Lets automatically close the mobile navigation when the
    // location path is changed.
    onClose();
  }, [location]);


  // Store current path as "continue shopping" target when not on checkout (include cart so Cart→Checkout→Continue goes to cart)
  useEffect(() => {
    const path = location.pathname + (location.search || '');

    if (path && path !== '/checkout' && !path.startsWith('/checkout/')) {
      storeContinueShoppingPath(path);
    }
  }, [location.pathname, location.search]);

  const onLogoClick = () => {
    onCloseSearchBox();
    // Goto the home page.
    const path = buildUrl(HOME_HREF);

    history.push(path);

    // Close the drawer.
    onClose();
  };

  const onCartClick = () => {
    onCloseSearchBox();
    // Close the nav drawer; cart flyout is opened directly by Header via CartFlyoutContext.
    onClose();
  };

  const onAccountClick = () => {
    onCloseSearchBox();
    // Navigate to /account if logged in, /login if not
    const path = customer?.isRegistered ? buildUrl('/account') : buildUrl('/login');

    history.push(path);
  };

  const onWishlistClick = () => {
    // Link to wishlist page if registered; Header component will show auth modal for guest users
    const path = buildUrl('/account/wishlist');

    history.push(path);
  };

  const onQuoteClick = () => {
    // TODO: Implement quote functionality
    // Link to quote page if registered; Header component will show auth modal for guest users
    console.log('onQuoteClick');
  };

  const trackPage = () => {
    activeData.trackPage(site.id, locale.id, currency);
  };

  useEffect(() => {
    trackPage();
  }, [location]);

  const navigationMenu = contentfulHeader?.mainNavigation;
  const menuItems = ensureArray(navigationMenu?.menuItemsCollection?.items).filter(Boolean);

  return (
    <Box className="sf-app" {...styles.container}>
      <SkipToContentLink />
      <StorefrontPreview getToken={getTokenWhenReady}>
        <GtmHead />
        <GtmPageView />
        <Helmet>
          {/* KINDW-247 — Adobe Typekit web fonts. Single, SSR-rendered source of
              truth for the font stylesheet (previously injected twice: a runtime
              loader in `vendors/aktiv-grotesk.js` and a per-page `<link>` in
              `components/seo`). Preconnect warms the TLS connection to the Typekit
              origins early so the font CSS + font files download sooner (better
              LCP/CLS). `crossOrigin` is required because the font files are fetched
              with CORS. `font-display`/TTL are controlled in the Adobe Fonts kit
              (not our code); see docs/performance/01-tsd-analysis.md §D6. */}
          <link crossOrigin="anonymous" href="https://use.typekit.net" rel="preconnect" />
          <link crossOrigin="anonymous" href="https://p.typekit.net" rel="preconnect" />
          <link crossOrigin="anonymous" href="https://use.typekit.net/kqo8fpw.css" rel="stylesheet" />
          {/* KINDW-247 (Async/Defer) — head-active_data.js only defines the
              `window.dw.ac` event-queue object (no dependencies). `defer` keeps
              it out of the render-blocking path: it runs after HTML parse but
              before DOMContentLoaded, so `dw.ac` is guaranteed to exist before
              React hydrates and `useActiveData` fires. The dwanalytics/dwac
              libraries below are `async` and drain the queue when they arrive.
              See docs/performance/01-tsd-analysis.md §D4. */}
          {ACTIVE_DATA_ENABLED && (
            <script
              defer
              id="headActiveData"
              src={getAssetUrl('static/head-active_data.js')}
              type="text/javascript"
            >
            </script>
          )}
          {/* KINDW-270 / KINDW-270.16 — Stripe.js v3 must load on every page so the
              SDK can collect device-fingerprint signals for fraud (per
              https://docs.stripe.com/js/including). Deferred so it doesn't block
              first paint; ~30 KB gzipped, browser-cached for 24 h.
              See [docs/payments/stripe/05-frontend-integration.md] §5.1. */}
          <script defer src="https://js.stripe.com/v3" />
        </Helmet>
        <IntlProvider
          defaultLocale={DEFAULT_LOCALE}
          locale={targetLocale}
          messages={messages}
          // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
          // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
          // - "extract-default-translations"
          // - "compile-translations:pseudo"
          onError={(err) => {
            if (!messages) {
              // During the ssr prepass phase the messages object has not loaded, so we can suppress
              // errors during this time.
              return;
            }
            if (err.code === 'MISSING_TRANSLATION') {
              // NOTE: Remove the console error for missing translations during development,
              // as we knew translations would be added later.
              logger.warn('Missing translation', {
                namespace: 'App.IntlProvider',
                additionalProperties: {
                  errorMessage: err.message,
                },
              });

              return;
            }
            throw err;
          }}
        >
          <CurrencyProvider currency={currency}>
            <Seo regionalSeoLinks={true}>
              <meta content={THEME_COLOR} name="theme-color" />
              <meta content={DEFAULT_SITE_TITLE} name="apple-mobile-web-app-title" />
              <link
                href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                rel="apple-touch-icon"
              />
              <link href={getAssetUrl('static/manifest.json')} rel="manifest" />
            </Seo>

            <ScrollToTop />

            <BlurOverlayProvider>
              <CartFlyoutProvider>
                <Box display="flex" flex={1} flexDirection="column" id="app">
                  <StoreLocatorModal
                    isOpen={isOpenStoreLocator}
                    onClose={onCloseStoreLocator}
                  />

                  {!isCheckout && (
                    <RegionRedirectModal
                      currentSiteId={site?.id}
                      isOpen={isRegionModalOpen}
                      suggestion={regionSuggestion}
                      onClose={dismissRegionSuggestion}
                      onStay={dismissRegionSuggestion}
                      onSwitch={confirmRegionSwitch}
                    />
                  )}

                  <CartFlyout />

                  {/* HeaderProvider manages header state for sticky, transparent, gradient, solid states.
                    Wraps both header and page content so child pages (e.g. PDP) can opt-in to hide-on-scroll. */}
                  <HeaderProvider configuredState={headerConfiguredState}>
                    <StickyHeaderBox
                      ref={headerWrapperRef}
                      isSearchBoxOpen={isOpenSearchBox}
                      isStickyDisabled={isStickyDisabled}
                    >
                      {!isCheckout && !isAuth ? (
                        <>
                          <AboveHeader />
                          <Header
                            className="header"
                            data-testid="header"
                            isMenuOpen={isOpen}
                            isSearchBoxOpen={isOpenSearchBox}
                            onLogoClick={onLogoClick}
                            onMenuClick={onOpen}
                            onMyAccountClick={onAccountClick}
                            onMyCartClick={onCartClick}
                            onSearchBoxClick={onOpenSearchBox}
                            onSearchBoxClose={onCloseSearchBox}
                            onStoreLocatorClick={onOpenStoreLocator}
                            onWishlistClick={onWishlistClick}
                          >
                            <HideOnDesktop>
                              <MegaMenuMobile
                                isLoading={isNavigationMenuLoading}
                                isOpen={isOpen}
                                menuItems={menuItems}
                                navigationMenu={navigationMenu}
                                onClose={onClose}
                                onLogoClick={onLogoClick}
                                onMyAccountClick={onAccountClick}
                                onMyQuoteClick={onQuoteClick}
                              />
                              {/* <DrawerMenu
                              isOpen={isOpen}
                              itemComponent={DrawerMenuItemWithData}
                              itemsCountKey="onlineSubCategoriesCount"
                              itemsKey="categories"
                              root={
                                categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                              }
                              onClose={onClose}
                              onLogoClick={onLogoClick}
                            /> */}
                            </HideOnDesktop>

                            <HideOnMobile>
                              <MegaMenuDesktop
                                isLoading={isNavigationMenuLoading}
                                menuItems={menuItems}
                                navigationMenu={navigationMenu}
                              />
                              {/* <ListMenu
                              contentComponent={ListMenuContentWithData}
                              itemsCountKey="onlineSubCategoriesCount"
                              itemsKey="categories"
                              root={
                                categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                              }
                            /> */}
                            </HideOnMobile>
                          </Header>
                        </>
                      ) : (
                        <>
                          {isCheckout ? (
                            <CheckoutHeader />
                          ) : null}
                        </>
                      )}
                    </StickyHeaderBox>
                    <SearchModal isOpen={isOpenSearchBox} onClose={onCloseSearchBox} />

                    {!isOnline && <OfflineBanner />}
                    <AddToCartModalProvider>
                      <SkipNavContent
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          outline: 0,
                        }}
                      >
                        <Box
                          as="main"
                          display="flex"
                          flex="1"
                          flexDirection="column"
                          id="app-main"
                          role="main"
                        >
                          <OfflineBoundary isOnline={false}>
                            <CalendlyTriggerProvider>
                              {children}
                            </CalendlyTriggerProvider>
                          </OfflineBoundary>
                        </Box>
                      </SkipNavContent>

                      {!isCheckout && !isAuth && <Footer />}

                      <AuthModal {...authModal} />
                      <DntNotification {...dntNotification} />

                      {/* Showroom Selection Popup - prompts user to select their showroom */}
                      {!isCheckout && !isAuth && <ShowroomSelectionPopup />}
                    </AddToCartModalProvider>
                  </HeaderProvider>
                </Box>
              </CartFlyoutProvider>
            </BlurOverlayProvider>
          </CurrencyProvider>
        </IntlProvider>
        {ACTIVE_DATA_ENABLED && (
          <script
            async="async"
            id="dwanalytics"
            src={getAssetUrl('static/dwanalytics-22.2.js')}
            type="text/javascript"
            onLoad={trackPage}
          >
          </script>
        )}
        {ACTIVE_DATA_ENABLED && (
          <script
            async="async"
            id="dwac"
            src={getAssetUrl('static/dwac-21.7.js')}
            type="text/javascript"
          >
          </script>
        )}
      </StorefrontPreview>
    </Box>
  );
};

App.propTypes = {
  children: PropTypes.node,
};

export default App;
